import { useFeatureFlag } from '../../app/_providers/ExperimentationProvider.client'
import { useTranslation } from '../../app/_providers/TranslationProvider.client'
import { isSufficientEmbroideryColorContrast } from '../../containers/EmbroideryContainer/utils/unorganized'

import { hexesAreEqual } from './utils'

export type EmbroideryColorFeedback = {
  message: string
  tone: 'caution' | 'positive'
  icon: 'warning' | 'thread'
}

/**
 * The line of copy shown alongside an embroidery thread color, wherever that color is chosen or
 * summarized.
 *
 * Control only speaks up when the thread and garment colors are too close in contrast to read.
 * With `monochrome-vibes-embroidery-text` on, an exact thread-on-garment color match reads
 * "Monochrome vibes"; anything else is silent unless the caller opts into praise.
 */
export function useEmbroideryColorFeedback({
  backgroundColor,
  threadColor,
  warnWhenThreadColorMissing = false,
  praiseWhenColorsDiffer = false,
}: {
  backgroundColor: string | undefined
  threadColor: string | undefined
  /**
   * Control-only: the color selector has always warned while no thread color is selected yet.
   * Opt in to preserve that; the flag-on experience stays quiet until there is a color to
   * comment on.
   */
  warnWhenThreadColorMissing?: boolean
  /**
   * Flag-on only: say "Lookin' good!" for a non-monochrome pairing. Reserved for the color
   * selector, where the praise answers a choice the customer just made. The summary surfaces
   * only speak up when there is something to flag.
   */
  praiseWhenColorsDiffer?: boolean
}): EmbroideryColorFeedback | null {
  const {
    embroidery: {
      validation: { colorContrastWarning, monochromeVibes, lookinGood },
    },
  } = useTranslation()

  const monochromeCopyFlag = useFeatureFlag<'true' | 'false'>({
    key: 'monochrome-vibes-embroidery-text',
    controlVariant: 'false',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
  })

  if (monochromeCopyFlag === 'true') {
    if (!threadColor) return null

    if (hexesAreEqual(backgroundColor, threadColor)) {
      return { message: monochromeVibes, tone: 'caution', icon: 'thread' }
    }

    return praiseWhenColorsDiffer ? { message: lookinGood, tone: 'positive', icon: 'thread' } : null
  }

  const isSufficientColorContrast = threadColor
    ? isSufficientEmbroideryColorContrast(backgroundColor, threadColor)
    : !warnWhenThreadColorMissing

  return isSufficientColorContrast
    ? null
    : { message: colorContrastWarning, tone: 'caution', icon: 'warning' }
}
